import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiOptionsModel }                  from '../api/models/api.options';
import { ApiService }                       from '../api/api.service';
import { BookingModel }                     from './typings/bookings.booking';
import { BookingsApiKeysModel }             from './typings/bookings.api-keys';
import { BookingsApiSecretKeyComponent }    from './bookings.api-secret-key.component';
import { BookingsAppointmentTypeComponent } from './bookings.appointment-type.component';
import { BookingsAppointmentTypeModel }     from './typings/bookings.appointment-type';
import { BookingsRequestComponent }         from './bookings.request.component';
import { BookingsRequestModel }             from './typings/bookings.request';
import { CodeTypeModel }                    from '../core/models/code-type';
import { CompanySettingsModel }             from '../companies/models/company.settings';
import { ConfirmService }                   from '../messages/confirm/confirm.service';
import { DateParserService }                from '../core/services/date-parser.service';
import { MessagesService }                  from '../messages/messages.service';
import { StreamSearchService }              from '../api/stream-search.service';
import { TableQueryParameters }             from '../table/types/table-query-parameters';
import { VerboseStreamResponseModel }       from '../api/models/stream.verbose-response';

@Injectable({
  providedIn: 'root'
})
export class BookingsService {
  public appointmentsTypesTableCacheKey = 'bookingAppointmentTypes';
  public requestTableCacheKey           = 'bookingRequests';

  constructor (
    private apiService         : ApiService,
    private confirmService     : ConfirmService,
    private dateParserService  : DateParserService,
    private messagesService    : MessagesService,
    private modalService       : NgbModal,
    private streamSearchService: StreamSearchService,
    private translateService   : TranslateService,
  ) {}

  addAllAppointmentsToVVGarage () : Promise<any> {
    return this.apiService.post('/vv_garage/add_appointment_types_to_vv_garage');
  }

  addAppointmentType ( appointmentType = {} as BookingsAppointmentTypeModel ) : Promise<string> {
    const modal =  this.modalService.open(BookingsAppointmentTypeComponent, {
      windowClass: 'lg-modal'
    });

    modal.componentInstance.appointmentType = appointmentType;

    return modal.result;
  }

  createAppointmentType ( appointmentType : BookingsAppointmentTypeModel ) : Promise<Array<BookingsAppointmentTypeModel>> {
    return this.apiService.post('/appointment_type', appointmentType);
  }

  confirm ( booking : BookingModel ) : Promise<any> {
    return this.apiService.post('/event_request/confirm_booking', {
        create_new_customer : booking.createNewCustomer,
        email_address       : booking.contact.email_address,
        email_subject       : booking.contact.subject,
        id                  : booking.request?.id,
        message_body        : booking.contact.body || booking.contact.message,
        mobile              : booking.contact.send_to,
        selected_customer_id: booking.selectedCustomerId,
        start_time          : booking.request?.local_start_time
      });
  }

  deleteAppointmentType  ( appointmentType : BookingsAppointmentTypeModel ) : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_APPOINTMENT_TYPE'))
      .then(() => this.apiService.delete('/appointment_type', appointmentType.id));
  }

  deleteRequest ( request : BookingsRequestModel ) : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_BOOKING_REQUEST'))
      .then(() => this.apiService.delete('/event_request', request.id));
  }

  formatLocalStartTime ( request : BookingsRequestModel ) : string {
    return this.dateParserService.format(`${ request.start_date } ${ request.start_time }`, `${ this.dateParserService.dateFormat }T${ this.dateParserService.timeFormat }Z`);
  }

  generateApiKey () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('GENERAL_SPACE.MESSAGE.GENERATE_NEW_KEY'))
      .then(() => this.apiService.get('/company_settings/generate_public_booking_api_key'))
      .then(( keys : BookingsApiKeysModel ) => this.getMaskedCompanyId()
        .then(( maskedCompanyId : string ) => {
          return {
            ...keys,
            masked_company_id: maskedCompanyId
          };
        }))
      .then(( keys : BookingsApiKeysModel ) => {
        this.messagesService.show(this.translateService.instant('GENERAL_SPACE.MESSAGE.NEW_API_KEY_GENERATED'), 'success');

        const modal =  this.modalService.open(BookingsApiSecretKeyComponent, {
          size: 'lg'
        });

        modal.componentInstance.keys = keys;

        return modal.result;
      });
  }

  getAppointmentTypes ( params ?: TableQueryParameters ) : Promise<Array<BookingsAppointmentTypeModel>> {
    return params
      ? this.apiService.get('/appointment_types', [
          params.offset   || 0,
          params.pageSize || 11
        ])
      : this.apiService.get('/system/event_appointment_types', null, 'appointmentTypes');
  }

  getAppointmentTypesCount () : Promise<number> {
    return this.apiService.get('appointment_types/count');
  }

  getBodyTypes () : Promise<Array<CodeTypeModel>> {
    return this.apiService.get('/system/vehicle_body_types', null, 'bodyTypes');
  }

  getContactTypes () : Promise<Array<CodeTypeModel>> {
    return this.apiService.get('/system/event_request_contact_types', null, 'contactTypes');
  }

  getMaskedCompanyId () : Promise<string> {
    return this.apiService.get('/company_settings/public_booking_api_company_id')
      .then(response => response.company_id);
  }

  getMechanics ( query : string ) : Promise<Array<any>> {
    const search = this.streamSearchService.search('mechanics', query, {
        verbose: true
      } as ApiOptionsModel) as VerboseStreamResponseModel;

    return search.promise;
  }

  getRequest ( id : string ) : Promise<BookingsRequestModel> {
    return this.apiService.get('/event_request', id);
  }

  getRequests ( params ?: TableQueryParameters ) : Promise<Array<BookingsRequestModel>> {
    return this.apiService.get('/event_requests', [
        params?.offset   || 0,
        params?.pageSize || 11,
        params?.field,
        params?.direction
      ]);
  }

  getRequestsCount () : Promise<number> {
    return this.apiService.get('/event_requests/count');
  }

  getSettings () : Promise<CompanySettingsModel> {
    return this.apiService.get('/company_settings');
  }

  updateAppointmentType ( appointmentType : BookingsAppointmentTypeModel ) : Promise<BookingsAppointmentTypeModel> {
    return this.apiService.patch('/appointment_type', appointmentType);
  }

  updateRequest ( request : BookingsRequestModel ) : Promise<BookingsRequestModel> {
    return this.apiService.patch('/event_request', request);
  }

  updateSettings ( settings : CompanySettingsModel ) : Promise<CompanySettingsModel> {
    return this.apiService.patch('/company_settings', settings, 'company_setting');
  }

  validateBookingTime ( request : BookingsRequestModel ) : Promise<any> {
    return this.apiService.post('/event_request/confirm_valid_booking_time', {
        local_start_time: this.formatLocalStartTime(request),
        mechanic_id     : request.mechanic_id
      });
  }

  viewDiary ( request : BookingsRequestModel ) : void {
    window.open(`workshop.html#/booking-diary?view=day&group_index=0&date=${
      request.requested_date
    }`,'_blank');
  }

  viewRequest ( request : BookingsRequestModel ) : Promise<any> {
    const promises : Array<Promise<any>> = [
      this.getAppointmentTypes(),
      this.getBodyTypes(),
      this.getContactTypes(),
      this.getRequest(request.id)
    ];

    return Promise.all(promises)
      .then(([ appointmentTypes, bodyTypes, contactTypes, request ]) => {
        const modal = this.modalService.open(BookingsRequestComponent, {
          windowClass: 'x-lg-modal'
        });

        modal.componentInstance.appointmentTypes = appointmentTypes;
        modal.componentInstance.bodyTypes        = bodyTypes;
        modal.componentInstance.contactTypes     = contactTypes;
        modal.componentInstance.request          = request;

        return modal.result;
      });
  }
}
